import { useScheduleChangeLog, type ScheduleChangeLog } from '@/hooks/useScheduleChangeLog';
import { formatDateFull } from '@/lib/scheduleUtils';

interface Props {
  projectId: string;
}

export default function ClientScheduleUpdates({ projectId }: Props) {
  const { logs, loading } = useScheduleChangeLog(projectId);

  const clientLogs = logs.filter((log) => log.is_client_visible);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <i className="ri-loader-4-line animate-spin text-[#059669] text-lg"></i>
      </div>
    );
  }

  if (clientLogs.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <button className="w-full flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          <i className="ri-notification-3-line text-[#E8621A]"></i>
          <span className="text-sm font-bold text-[#1A1A1A]">Schedule Updates</span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
            {clientLogs.length}
          </span>
        </div>
      </button>
      <div className="px-5 pb-4 space-y-3">
        {clientLogs.slice(0, 8).map((log, i) => (
          <div key={log.id} className="flex items-start gap-3">
            <div className="flex flex-col items-center flex-shrink-0 w-6">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center border-2 text-[10px] font-bold"
                style={{
                  backgroundColor: '#F0FDF4',
                  borderColor: '#BBF7D0',
                  color: '#059669',
                }}
              >
                {i + 1}
              </div>
              {i < clientLogs.length - 1 && (
                <div className="w-0.5 flex-1 min-h-[12px] bg-[#E5E7EB]"></div>
              )}
            </div>
            <div className={`flex-1 min-w-0 ${i < clientLogs.length - 1 ? 'pb-3' : 'pb-0'}`}>
              <p className="text-sm font-semibold text-[#1A1A1A]">
                {log.schedule_item_title || 'Project schedule update'}
              </p>
              <p className="text-[10px] text-[#9CA3AF] capitalize mt-0.5">
                {log.change_type.replace(/_/g, ' ')} · {new Date(log.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              {log.new_start_date && (
                <p className="text-xs text-[#6B7280] mt-1">
                  Updated date: {formatDateFull(log.new_start_date)}
                  {log.new_end_date ? ` — ${formatDateFull(log.new_end_date)}` : ''}
                </p>
              )}
              {log.change_reason && (
                <p className="text-xs text-[#6B7280] mt-1 bg-[#F9FAFB] rounded-lg px-2 py-1">
                  {log.change_reason}
                </p>
              )}
              {log.affected_item_count > 0 && (
                <p className="text-[10px] text-[#E8621A] font-medium mt-0.5">
                  {log.affected_item_count} related schedule item{log.affected_item_count !== 1 ? 's' : ''} updated
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}